<template>
  <div class="dialog">
    <span class="cls" @click="close"></span>
    <div class="paper">
      <!-- Header -->
      <div class="header">
        <h2>내 자료</h2>
        <span class="close" @click="close">
          <i class="ph ph-x"></i>
        </span>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">
        <div class="toolbar-left">
          <div class="search-input">
            <i class="ph ph-magnifying-glass"></i>
            <input 
              type="text" 
              v-model="searchKeyword" 
              placeholder="파일명 검색"
              @input="filterFiles"
            />
            <i 
              class="ph ph-x clear-btn" 
              v-if="searchKeyword" 
              @click="clearSearch"
            ></i>
          </div>
        </div>
        <div class="toolbar-right">
          <!-- 뷰 전환 -->
          <div class="view-toggle">
            <button 
              :class="{ active: viewMode === 'grid' }" 
              @click="viewMode = 'grid'"
            >
              <i class="ph ph-squares-four"></i>
            </button>
            <button 
              :class="{ active: viewMode === 'list' }" 
              @click="viewMode = 'list'"
            >
              <i class="ph ph-list"></i>
            </button>
          </div>
          <!-- 정렬 -->
          <button class="sort-btn" @click="toggleSortMenu">
            <i class="ph ph-sort-ascending"></i>
          </button>
          <div class="sort-menu" v-if="showSortMenu">
            <div 
              class="sort-option" 
              v-for="option in sortOptions" 
              :key="option.value"
              :class="{ active: activeSort === option.value }"
              @click="changeSort(option.value)"
            >
              <i :class="option.icon"></i>
              {{ option.label }}
            </div>
          </div>
          <!-- 업로드 -->
          <button class="upload-btn" @click="triggerUpload">
            <i class="ph ph-upload-simple"></i>
            <span>업로드</span>
          </button>
          <input 
            type="file" 
            ref="fileInput" 
            multiple 
            @change="handleUpload" 
            style="display: none"
          />
        </div>
      </div>

      <!-- Storage Bar -->
      <div class="storage-bar">
        <div class="storage-info">
          <span class="storage-label">저장공간</span>
          <span class="storage-detail">{{ formatFileSize(storageUsed) }} / {{ formatFileSize(storageLimit) }} 사용</span>
        </div>
        <div class="bar-track">
          <div 
            class="bar-fill" 
            :style="{ width: storagePercent + '%' }"
            :class="{ warning: storagePercent > 80, danger: storagePercent > 95 }"
          ></div>
        </div>
      </div>

      <!-- Content -->
      <div class="content">
        <!-- 선택 모드 헤더 -->
        <div class="selection-bar" v-if="selectedFiles.length > 0">
          <span class="selection-count">{{ selectedFiles.length }}개 선택됨</span>
          <div class="selection-actions">
            <button @click="downloadSelected">
              <i class="ph ph-download-simple"></i>
            </button>
            <button @click="deleteSelected" class="danger">
              <i class="ph ph-trash"></i>
            </button>
            <button @click="clearSelection">
              <i class="ph ph-x"></i>
            </button>
          </div>
        </div>

        <!-- 빈 상태 -->
        <div class="empty-state" v-if="filteredFiles.length === 0 && !isLoading">
          <i class="ph ph-folder-open"></i>
          <p v-if="searchKeyword">검색 결과가 없습니다</p>
          <p v-else>등록된 자료가 없습니다</p>
          <button v-if="!searchKeyword" @click="triggerUpload">
            <i class="ph ph-upload-simple"></i>
            파일 업로드
          </button>
        </div>

        <!-- 로딩 -->
        <div class="loading-state" v-if="isLoading">
          <span class="loading-spinner"></span>
          <p>불러오는 중...</p>
        </div>

        <!-- 그리드 뷰 -->
        <div class="file-grid" v-if="viewMode === 'grid' && !isLoading && filteredFiles.length > 0">
          <div 
            class="file-card" 
            v-for="file in filteredFiles" 
            :key="file.idx"
            :class="{ selected: isSelected(file.idx) }"
            @click="handleFileClick(file, $event)"
            @contextmenu.prevent="showContextMenu(file, $event)"
          >
            <div class="file-checkbox" @click.stop="toggleSelect(file.idx)">
              <i :class="isSelected(file.idx) ? 'ph-fill ph-check-square' : 'ph ph-square'"></i>
            </div>
            <div class="file-preview">
              <img v-if="isImageFile(file.type)" :src="file.path" :alt="file.title" />
              <div v-else class="file-icon-large">
                <i :class="getFileIcon(file.type)"></i>
              </div>
            </div>
            <div class="file-info">
              <span class="file-name">{{ file.title }}</span>
              <span class="file-meta">{{ formatFileSize(file.size) }}</span>
            </div>
          </div>
        </div>

        <!-- 리스트 뷰 -->
        <div class="file-list" v-if="viewMode === 'list' && !isLoading && filteredFiles.length > 0">
          <div class="list-header">
            <span class="col-check"></span>
            <span class="col-name">이름</span>
            <span class="col-size">크기</span>
            <span class="col-date">수정일</span>
            <span class="col-action"></span>
          </div>
          <div 
            class="list-item" 
            v-for="file in filteredFiles" 
            :key="file.idx"
            :class="{ selected: isSelected(file.idx) }"
            @click="handleFileClick(file, $event)"
            @contextmenu.prevent="showContextMenu(file, $event)"
          >
            <span class="col-check" @click.stop="toggleSelect(file.idx)">
              <i :class="isSelected(file.idx) ? 'ph-fill ph-check-square' : 'ph ph-square'"></i>
            </span>
            <span class="col-name">
              <i :class="getFileIcon(file.type)"></i>
              {{ file.title }}
            </span>
            <span class="col-size">{{ formatFileSize(file.size) }}</span>
            <span class="col-date">{{ formatDate(file.created_at) }}</span>
            <span class="col-action" @click.stop="showContextMenu(file, $event)">
              <i class="ph ph-dots-three"></i>
            </span>
          </div>
        </div>

        <!-- Load More -->
        <div class="load-more" v-if="hasMore && !isLoading" ref="loadMoreRef">
          <span class="loading-spinner"></span>
        </div>
      </div>

      <!-- Context Menu -->
      <div 
        class="context-menu" 
        v-if="contextMenu.show"
        :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
      >
        <button @click="previewFile(contextMenu.file)">
          <i class="ph ph-eye"></i>
          미리보기
        </button>
        <button @click="downloadFile(contextMenu.file)">
          <i class="ph ph-download-simple"></i>
          다운로드
        </button>
        <button @click="renameFile(contextMenu.file)">
          <i class="ph ph-pencil-simple"></i>
          이름 변경
        </button>
        <div class="menu-divider"></div>
        <button @click="deleteFile(contextMenu.file)" class="danger">
          <i class="ph ph-trash"></i>
          삭제
        </button>
      </div>

      <!-- Image Viewer -->
      <div class="image-viewer" v-if="previewImage" @click="closePreview">
        <div class="viewer-header">
          <span class="viewer-title">{{ previewImage.title }}</span>
          <button class="viewer-close" @click="closePreview">
            <i class="ph ph-x"></i>
          </button>
        </div>
        <div class="viewer-body" @click.stop>
          <button class="viewer-nav prev" @click="prevImage" v-if="imageFiles.length > 1">
            <i class="ph ph-caret-left"></i>
          </button>
          <img :src="previewImage.path" :alt="previewImage.title" />
          <button class="viewer-nav next" @click="nextImage" v-if="imageFiles.length > 1">
            <i class="ph ph-caret-right"></i>
          </button>
        </div>
        <div class="viewer-footer">
          <span class="viewer-meta">{{ formatFileSize(previewImage.size) }} · {{ formatDate(previewImage.created_at) }}</span>
          <div class="viewer-actions">
            <button @click="downloadFile(previewImage)">
              <i class="ph ph-download-simple"></i>
            </button>
            <button @click="deleteFile(previewImage)">
              <i class="ph ph-trash"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Upload Progress -->
      <div class="upload-overlay" v-if="isUploading">
        <div class="upload-modal">
          <span class="loading-spinner large"></span>
          <p>업로드 중... {{ uploadProgress }}%</p>
          <div class="upload-bar">
            <div class="upload-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
        </div>
      </div>

      <!-- Rename Dialog -->
      <div class="rename-overlay" v-if="renameDialog.show" @click="cancelRename">
        <div class="rename-modal" @click.stop>
          <h3>이름 변경</h3>
          <input 
            type="text" 
            v-model="renameDialog.newName" 
            @keyup.enter="confirmRename"
            ref="renameInput"
          />
          <div class="rename-actions">
            <button class="cancel" @click="cancelRename">취소</button>
            <button class="confirm" @click="confirmRename">확인</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, inject, onMounted, onUnmounted, nextTick } from "vue"

export default {
  props: {
    oth_showMyFiles: Function
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')
    const mb_id = member_info.mb_id

    // 상태
    const fileList = ref([])
    const filteredFiles = ref([])
    const searchKeyword = ref('')
    const viewMode = ref('grid')
    const isLoading = ref(false)
    const hasMore = ref(true)
    const loadMoreRef = ref(null)
    let observer = null

    // 페이지네이션
    const currentPage = ref(1)
    const perPage = 24

    // 정렬
    const sortOptions = [
      { label: '최신순', value: 'recent', icon: 'ph ph-clock' },
      { label: '이름순', value: 'name', icon: 'ph ph-sort-ascending' },
      { label: '크기순', value: 'size', icon: 'ph ph-database' },
      { label: '형식순', value: 'type', icon: 'ph ph-file' }
    ]
    const activeSort = ref('recent')
    const showSortMenu = ref(false)

    // 선택
    const selectedFiles = ref([])

    // 용량
    const storageUsed = ref(0)
    const storageLimit = ref(1 * 1024 * 1024 * 1024)
    const storagePercent = ref(0)

    // 업로드
    const fileInput = ref(null)
    const isUploading = ref(false)
    const uploadProgress = ref(0)

    // 미리보기
    const previewImage = ref(null)

    // 컨텍스트 메뉴
    const contextMenu = ref({
      show: false,
      x: 0,
      y: 0,
      file: null
    })

    // 이름 변경
    const renameDialog = ref({
      show: false,
      file: null,
      newName: ''
    })
    const renameInput = ref(null)

    // 이미지 파일 목록 (뷰어 네비게이션용)
    const imageFiles = computed(() => {
      return filteredFiles.value.filter(f => isImageFile(f.type))
    })

    // 파일 목록 조회
    const fetchFiles = async (reset = false) => {
      if (isLoading.value) return
      isLoading.value = true

      if (reset) {
        currentPage.value = 1
        fileList.value = []
        hasMore.value = true
      }

      let orderBy = 'created_at DESC'
      if (activeSort.value === 'name') orderBy = 'title ASC'
      else if (activeSort.value === 'size') orderBy = 'size DESC'
      else if (activeSort.value === 'type') orderBy = 'type ASC, title ASC'

      const offset = (currentPage.value - 1) * perPage
      const rows = await sql_query(`
        SELECT * FROM files 
        WHERE uploaded_by = '${mb_id}'
        AND target_table = 'customers'
        AND status = 1
        ORDER BY ${orderBy}
        LIMIT ${perPage} OFFSET ${offset}
      `)

      if (rows.length < perPage) {
        hasMore.value = false
      }

      fileList.value = [...fileList.value, ...rows]
      filterFiles()
      currentPage.value++
      isLoading.value = false
    }

    // 검색 필터
    const filterFiles = () => {
      if (!searchKeyword.value.trim()) {
        filteredFiles.value = fileList.value
      } else {
        const keyword = searchKeyword.value.toLowerCase()
        filteredFiles.value = fileList.value.filter(f => 
          f.title.toLowerCase().includes(keyword)
        )
      }
    }

    const clearSearch = () => {
      searchKeyword.value = ''
      filterFiles()
    }

    // 용량 조회
    const fetchStorageInfo = async () => {
      try {
        const response = await fetch('/api/user/storage', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user: mb_id })
        })
        const data = await response.json()
        if (data.success) {
          storageUsed.value = data.storage.used
          storageLimit.value = data.storage.limit
          storagePercent.value = parseFloat(data.storage.usedPercent)
        }
      } catch (err) {
        console.error('용량 조회 실패:', err)
      }
    }

    // 정렬 변경
    const toggleSortMenu = () => {
      showSortMenu.value = !showSortMenu.value
    }

    const changeSort = (sort) => {
      activeSort.value = sort
      showSortMenu.value = false
      fetchFiles(true)
    }

    // 선택 관련
    const isSelected = (idx) => selectedFiles.value.includes(idx)
    
    const toggleSelect = (idx) => {
      const index = selectedFiles.value.indexOf(idx)
      if (index > -1) {
        selectedFiles.value.splice(index, 1)
      } else {
        selectedFiles.value.push(idx)
      }
    }

    const clearSelection = () => {
      selectedFiles.value = []
    }

    // 파일 클릭
    const handleFileClick = (file, event) => {
      if (event.ctrlKey || event.metaKey) {
        toggleSelect(file.idx)
      } else if (event.shiftKey && selectedFiles.value.length > 0) {
        // Shift 선택
        const lastSelected = selectedFiles.value[selectedFiles.value.length - 1]
        const lastIndex = filteredFiles.value.findIndex(f => f.idx === lastSelected)
        const currentIndex = filteredFiles.value.findIndex(f => f.idx === file.idx)
        const [start, end] = lastIndex < currentIndex ? [lastIndex, currentIndex] : [currentIndex, lastIndex]
        
        for (let i = start; i <= end; i++) {
          const idx = filteredFiles.value[i].idx
          if (!selectedFiles.value.includes(idx)) {
            selectedFiles.value.push(idx)
          }
        }
      } else {
        // 일반 클릭 - 미리보기
        if (isImageFile(file.type)) {
          previewImage.value = file
        } else {
          window.open(file.path, '_blank')
        }
      }
    }

    // 컨텍스트 메뉴
    const showContextMenu = (file, event) => {
      contextMenu.value = {
        show: true,
        x: Math.min(event.clientX, window.innerWidth - 160),
        y: Math.min(event.clientY, window.innerHeight - 200),
        file
      }
    }

    const hideContextMenu = () => {
      contextMenu.value.show = false
    }

    // 미리보기
    const previewFile = (file) => {
      hideContextMenu()
      if (isImageFile(file.type)) {
        previewImage.value = file
      } else {
        window.open(file.path, '_blank')
      }
    }

    const closePreview = () => {
      previewImage.value = null
    }

    const prevImage = () => {
      const idx = imageFiles.value.findIndex(f => f.idx === previewImage.value.idx)
      if (idx > 0) {
        previewImage.value = imageFiles.value[idx - 1]
      } else {
        previewImage.value = imageFiles.value[imageFiles.value.length - 1]
      }
    }

    const nextImage = () => {
      const idx = imageFiles.value.findIndex(f => f.idx === previewImage.value.idx)
      if (idx < imageFiles.value.length - 1) {
        previewImage.value = imageFiles.value[idx + 1]
      } else {
        previewImage.value = imageFiles.value[0]
      }
    }

    // 업로드
    const triggerUpload = () => {
      fileInput.value?.click()
    }

    const handleUpload = async (event) => {
      const files = event.target.files
      if (!files || files.length === 0) return

      // 용량 미리 체크
      let totalSize = 0
      for (let i = 0; i < files.length; i++) {
        totalSize += files[i].size
      }
      
      if (storageUsed.value + totalSize > storageLimit.value) {
        alert('저장 용량이 부족합니다.')
        event.target.value = ''
        return
      }

      isUploading.value = true
      uploadProgress.value = 0

      const formData = new FormData()
      
      // ✅ user, target_table을 files보다 먼저 append
      formData.append('user', mb_id)
      formData.append('target_table', 'customers')
      
      // 그 다음 files
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i])
      }

      const xhr = new XMLHttpRequest()
      
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          uploadProgress.value = Math.round((e.loaded / e.total) * 100)
        }
      }

      xhr.onload = () => {
        isUploading.value = false
        
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText)
          if (response.success) {
            fetchFiles(true)
            fetchStorageInfo()
          } else {
            alert(response.message || '업로드 실패')
          }
        } else if (xhr.status === 413) {
          alert('저장 용량을 초과했습니다.')
        } else {
          alert('업로드 실패')
        }
      }

      xhr.onerror = () => {
        isUploading.value = false
        alert('업로드 중 오류가 발생했습니다.')
      }

      xhr.open('POST', '/api/files/upload')
      xhr.send(formData)

      event.target.value = ''
    }

    // 다운로드
    const downloadFile = (file) => {
      hideContextMenu()
      const link = document.createElement('a')
      link.href = file.path
      link.download = file.title
      link.click()
    }

    const downloadSelected = () => {
      selectedFiles.value.forEach(idx => {
        const file = fileList.value.find(f => f.idx === idx)
        if (file) downloadFile(file)
      })
      clearSelection()
    }

    // 이름 변경
    const renameFile = async (file) => {
      hideContextMenu()
      renameDialog.value = {
        show: true,
        file,
        newName: file.title
      }
      await nextTick()
      renameInput.value?.focus()
      renameInput.value?.select()
    }

    const confirmRename = async () => {
      if (!renameDialog.value.newName.trim()) return
      
      try {
        await sql_query(`
          UPDATE files SET title = '${renameDialog.value.newName.replace(/'/g, "''")}'
          WHERE idx = ${renameDialog.value.file.idx}
        `)
        
        const file = fileList.value.find(f => f.idx === renameDialog.value.file.idx)
        if (file) file.title = renameDialog.value.newName
        filterFiles()
      } catch (err) {
        alert('이름 변경 실패')
      }
      
      cancelRename()
    }

    const cancelRename = () => {
      renameDialog.value.show = false
    }

    // 삭제
    const deleteFile = async (file) => {
      hideContextMenu()
      closePreview()
      
      if (!confirm(`"${file.title}" 파일을 삭제하시겠습니까?`)) return

      try {
        await sql_query(`
          UPDATE files SET status = 0 WHERE idx = ${file.idx}
        `)
        fileList.value = fileList.value.filter(f => f.idx !== file.idx)
        filterFiles()
        fetchStorageInfo()
      } catch (err) {
        alert('삭제 실패')
      }
    }

    const deleteSelected = async () => {
      if (!confirm(`${selectedFiles.value.length}개 파일을 삭제하시겠습니까?`)) return

      try {
        const ids = selectedFiles.value.join(',')
        await sql_query(`UPDATE files SET status = 0 WHERE idx IN (${ids})`)
        fileList.value = fileList.value.filter(f => !selectedFiles.value.includes(f.idx))
        filterFiles()
        fetchStorageInfo()
        clearSelection()
      } catch (err) {
        alert('삭제 실패')
      }
    }

    // 유틸리티
    const isImageFile = (type) => {
      if (!type) return false
      return type.startsWith('image/')
    }

    const getFileIcon = (type) => {
      if (!type) return 'ph ph-file'
      if (type.startsWith('image/')) return 'ph ph-image'
      if (type.includes('pdf')) return 'ph ph-file-pdf'
      if (type.includes('word') || type.includes('document')) return 'ph ph-file-doc'
      if (type.includes('excel') || type.includes('sheet')) return 'ph ph-file-xls'
      if (type.includes('powerpoint') || type.includes('presentation')) return 'ph ph-file-ppt'
      if (type.includes('text')) return 'ph ph-file-text'
      if (type.includes('zip') || type.includes('rar')) return 'ph ph-file-zip'
      if (type.includes('video')) return 'ph ph-file-video'
      if (type.includes('audio')) return 'ph ph-file-audio'
      return 'ph ph-file'
    }

    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date

      if (diff < 86400000) {
        return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })
      }
      if (diff < 604800000) {
        return `${Math.floor(diff / 86400000)}일 전`
      }
      return `${date.getMonth() + 1}월 ${date.getDate()}일`
    }

    const close = () => {
      props.oth_showMyFiles(false)
    }

    // Intersection Observer
    const setupObserver = () => {
      observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore.value && !isLoading.value) {
          fetchFiles()
        }
      }, { threshold: 0.1 })

      if (loadMoreRef.value) {
        observer.observe(loadMoreRef.value)
      }
    }

    // 외부 클릭 핸들러
    const handleClickOutside = (e) => {
      if (!e.target.closest('.sort-btn') && !e.target.closest('.sort-menu')) {
        showSortMenu.value = false
      }
      if (!e.target.closest('.context-menu')) {
        hideContextMenu()
      }
    }

    // 키보드 단축키
    const handleKeydown = (e) => {
      if (e.key === 'Escape') {
        if (previewImage.value) closePreview()
        else if (renameDialog.value.show) cancelRename()
        else if (selectedFiles.value.length > 0) clearSelection()
      }
      if (previewImage.value) {
        if (e.key === 'ArrowLeft') prevImage()
        if (e.key === 'ArrowRight') nextImage()
      }
    }

    onMounted(() => {
      fetchFiles()
      fetchStorageInfo()
      setTimeout(setupObserver, 100)
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('keydown', handleKeydown)
    })

    onUnmounted(() => {
      if (observer) observer.disconnect()
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keydown', handleKeydown)
    })

    return {
      fileList,
      filteredFiles,
      searchKeyword,
      viewMode,
      isLoading,
      hasMore,
      loadMoreRef,
      sortOptions,
      activeSort,
      showSortMenu,
      selectedFiles,
      storageUsed,
      storageLimit,
      storagePercent,
      fileInput,
      isUploading,
      uploadProgress,
      previewImage,
      imageFiles,
      contextMenu,
      renameDialog,
      renameInput,
      filterFiles,
      clearSearch,
      toggleSortMenu,
      changeSort,
      isSelected,
      toggleSelect,
      clearSelection,
      handleFileClick,
      showContextMenu,
      hideContextMenu,
      previewFile,
      closePreview,
      prevImage,
      nextImage,
      triggerUpload,
      handleUpload,
      downloadFile,
      downloadSelected,
      renameFile,
      confirmRename,
      cancelRename,
      deleteFile,
      deleteSelected,
      isImageFile,
      getFileIcon,
      formatFileSize,
      formatDate,
      close
    }
  }
}
</script>

<style scoped>
.dialog {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: var(--z-index-top);
  backdrop-filter: blur(var(--effect-blur));
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  position: relative;
  width: 90%;
  max-width: 900px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  position: relative;
  flex-shrink: 0;
  border-bottom: 1px solid var(--color-background-light);
}

.header > h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
}

.header > .close {
  position: absolute;
  right: 16px;
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: var(--radius-sm);
}

.header > .close:active {
  background: var(--color-background-mute);
}

.header > .close > i {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

/* Toolbar */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  flex-shrink: 0;
  position: relative;
}

.toolbar-left {
  flex: 1;
  max-width: 300px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  background: var(--color-background-mute);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
}

.search-input > i {
  color: var(--color-text-third);
  font-size: var(--font-size-lg);
}

.search-input > input {
  flex: 1;
  border: none;
  background: none;
  padding: 10px 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  outline: none;
}

.search-input > input::placeholder {
  color: var(--color-text-third);
}

.search-input > .clear-btn {
  cursor: pointer;
  font-size: var(--font-size-base);
}

.view-toggle {
  display: flex;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.view-toggle button {
  width: 38px;
  height: 38px;
  border: none;
  background: var(--color-background);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-toggle button > i {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
}

.view-toggle button.active {
  background: var(--color-brand);
}

.view-toggle button.active > i {
  color: var(--color-text-white);
}

.sort-btn {
  width: 38px;
  height: 38px;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  background: var(--color-background);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sort-btn > i {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
}

.sort-menu {
  position: absolute;
  top: 100%;
  right: 80px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 10;
  min-width: 140px;
}

.sort-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
}

.sort-option:hover {
  background: var(--color-background-mute);
}

.sort-option.active {
  color: var(--color-brand);
  font-weight: var(--font-weight-medium);
}

.upload-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  height: 38px;
  border: none;
  border-radius: var(--radius-md);
  background: var(--color-brand);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-white);
  cursor: pointer;
}

.upload-btn > i {
  font-size: var(--font-size-lg);
}

/* Storage Bar */
.storage-bar {
  padding: 0 16px 12px;
  flex-shrink: 0;
}

.storage-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.storage-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.storage-detail {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
}

.bar-track {
  height: 4px;
  background: var(--color-background-mute);
  border-radius: 2px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: var(--color-brand);
  border-radius: 2px;
  transition: width 0.3s;
}

.bar-fill.warning {
  background: var(--color-special-warning);
}

.bar-fill.danger {
  background: #e53935;
}

/* Content */
.content {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
}

/* Selection Bar */
.selection-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: var(--color-brand-light);
  border-radius: var(--radius-md);
  margin-bottom: 12px;
}

.selection-count {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-brand);
}

.selection-actions {
  display: flex;
  gap: 4px;
}

.selection-actions button {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selection-actions button > i {
  font-size: var(--font-size-lg);
  color: var(--color-brand);
}

.selection-actions button.danger > i {
  color: var(--color-special-warning);
}

/* Empty & Loading */
.empty-state,
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 12px;
}

.empty-state > i,
.loading-state > i {
  font-size: 56px;
  color: var(--color-text-third);
}

.empty-state > p,
.loading-state > p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.empty-state > button {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: var(--radius-md);
  background: var(--color-brand);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-white);
  cursor: pointer;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--color-background-light);
  border-top-color: var(--color-brand);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-spinner.large {
  width: 40px;
  height: 40px;
  border-width: 3px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* File Grid */
.file-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.file-card {
  position: relative;
  background: var(--color-background-mute);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
}

.file-card:hover {
  border-color: var(--color-brand);
}

.file-card.selected {
  border-color: var(--color-brand);
  background: var(--color-brand-light);
}

.file-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 1;
}

.file-card:hover .file-checkbox,
.file-card.selected .file-checkbox {
  opacity: 1;
}

.file-checkbox > i {
  font-size: var(--font-size-lg);
  color: #fff;
}

.file-preview {
  width: 100%;
  aspect-ratio: 1;
  background: var(--color-background);
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-preview > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-icon-large {
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-icon-large > i {
  font-size: 48px;
  color: var(--color-brand);
}

.file-info {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-name {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-meta {
  font-size: 10px;
  color: var(--color-text-third);
}

/* File List */
.file-list {
  display: flex;
  flex-direction: column;
}

.list-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: var(--color-background-mute);
  border-radius: var(--radius-md);
  margin-bottom: 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
}

.list-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid var(--color-background-light);
  cursor: pointer;
  transition: background 0.2s;
}

.list-item:hover {
  background: var(--color-background-mute);
}

.list-item.selected {
  background: var(--color-brand-light);
}

.col-check {
  width: 32px;
  flex-shrink: 0;
}

.col-check > i {
  font-size: var(--font-size-lg);
  color: var(--color-text-third);
}

.list-item.selected .col-check > i {
  color: var(--color-brand);
}

.col-name {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.col-name > i {
  font-size: var(--font-size-xl);
  color: var(--color-brand);
  flex-shrink: 0;
}

.col-size {
  width: 80px;
  flex-shrink: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
  text-align: right;
}

.col-date {
  width: 80px;
  flex-shrink: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
  text-align: right;
}

.col-action {
  width: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.col-action > i {
  font-size: var(--font-size-lg);
  color: var(--color-text-third);
}

/* Load More */
.load-more {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

/* Context Menu */
.context-menu {
  position: fixed;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 100;
  min-width: 150px;
}

.context-menu button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  cursor: pointer;
  text-align: left;
}

.context-menu button:hover {
  background: var(--color-background-mute);
}

.context-menu button.danger {
  color: var(--color-special-warning);
}

.context-menu button > i {
  font-size: var(--font-size-base);
}

.menu-divider {
  height: 1px;
  background: var(--color-background-light);
  margin: 4px 0;
}

/* Image Viewer */
.image-viewer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  flex-direction: column;
  z-index: 100;
}

.viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  flex-shrink: 0;
}

.viewer-title {
  font-size: var(--font-size-sm);
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.viewer-close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-close > i {
  font-size: 24px;
  color: #fff;
}

.viewer-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0 60px;
  min-height: 0;
}

.viewer-body > img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: var(--radius-md);
}

.viewer-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-nav.prev { left: 8px; }
.viewer-nav.next { right: 8px; }

.viewer-nav > i {
  font-size: 24px;
  color: #fff;
}

.viewer-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  flex-shrink: 0;
}

.viewer-meta {
  font-size: var(--font-size-xs);
  color: rgba(255, 255, 255, 0.6);
}

.viewer-actions {
  display: flex;
  gap: 8px;
}

.viewer-actions button {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-actions button > i {
  font-size: var(--font-size-lg);
  color: #fff;
}

/* Upload Overlay */
.upload-overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.upload-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px;
  background: var(--color-background);
  border-radius: var(--radius-lg);
  min-width: 200px;
}

.upload-modal > p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
}

.upload-bar {
  width: 100%;
  height: 4px;
  background: var(--color-background-mute);
  border-radius: 2px;
  overflow: hidden;
}

.upload-fill {
  height: 100%;
  background: var(--color-brand);
  transition: width 0.2s;
}

/* Rename Dialog */
.rename-overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.rename-modal {
  padding: 24px;
  background: var(--color-background);
  border-radius: var(--radius-lg);
  min-width: 300px;
}

.rename-modal > h3 {
  margin: 0 0 16px 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.rename-modal > input {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  background: var(--color-background);
  outline: none;
  box-sizing: border-box;
}

.rename-modal > input:focus {
  border-color: var(--color-brand);
}

.rename-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.rename-actions button {
  padding: 10px 20px;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}

.rename-actions .cancel {
  background: var(--color-background-mute);
  color: var(--color-text-secondary);
}

.rename-actions .confirm {
  background: var(--color-brand);
  color: var(--color-text-white);
}

/* Mobile */
@media (max-width: 600px) {
  .toolbar {
    flex-wrap: wrap;
  }
  
  .toolbar-left {
    width: 100%;
    max-width: none;
    order: 2;
  }
  
  .toolbar-right {
    width: 100%;
    justify-content: flex-end;
    order: 1;
    margin-bottom: 8px;
  }

  .file-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .col-size,
  .col-date {
    display: none;
  }
}
</style>
